<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12" sm="8">
        <h1 class="text-h4 font-weight-bold primary--text">
          Listado de Convenios
        </h1>
        <p class="text-subtitle2 grey--text mt-2">
          Ver y gestionar todos los convenios generados
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="text-sm-right">
        <v-btn color="primary" to="/golf/convenios">
          <v-icon left>mdi-plus</v-icon>
          Nuevo Convenio
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="mb-6 elevation-2">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filtros.campo"
              :items="campos"
              item-text="nombre"
              item-value="id"
              label="Filtrar por Campo"
              outlined
              dense
              clearable
              @input="aplicarFiltros"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filtros.tipo"
              :items="tiposConvenios"
              item-text="nombre"
              item-value="id"
              label="Filtrar por Tipo"
              outlined
              dense
              clearable
              @input="aplicarFiltros"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filtros.estado"
              :items="[
                { text: 'Activo', value: 'activo' },
                { text: 'Vencido', value: 'vencido' },
                { text: 'Cancelado', value: 'cancelado' }
              ]"
              item-text="text"
              item-value="value"
              label="Filtrar por Estado"
              outlined
              dense
              clearable
              @input="aplicarFiltros"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filtros.socio"
              label="Buscar Socio"
              outlined
              dense
              prepend-icon="mdi-magnify"
              clearable
              @input="aplicarFiltros"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabla de Convenios -->
    <v-card class="elevation-2">
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

      <v-card-title>
        Convenios Generados
        <v-spacer></v-spacer>
        <v-chip color="primary" small text-color="white">
          {{ conveniosFiltrados.length }}
        </v-chip>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="conveniosFiltrados"
        :loading="loading"
        class="elevation-0"
      >
        <template v-slot:item.campo="{ item }">
          <div class="font-weight-bold">{{ getNombreCampo(item.campo_id) }}</div>
        </template>

        <template v-slot:item.tipo_detail.nombre="{ item }">
          <v-chip :color="getTipoColor(item.tipo)" text-color="white" small>
            {{ item.tipo_detail?.nombre || 'Desconocido' }}
          </v-chip>
        </template>

        <template v-slot:item.socio_detail.first_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" class="mr-2">
              <span>{{ (item.socio_detail?.first_name?.[0] || '') + (item.socio_detail?.last_name?.[0] || '') }}</span>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-caption">{{ item.socio_detail?.first_name }} {{ item.socio_detail?.last_name }}</div>
              <div class="text-caption grey--text">{{ item.socio_detail?.email }}</div>
            </div>
          </div>
        </template>

        <template v-slot:item.estado="{ item }">
          <v-chip
            :color="item.estado === 'activo' ? 'success' : item.estado === 'vencido' ? 'warning' : 'error'"
            text-color="white"
            small
          >
            {{ item.estado }}
          </v-chip>
        </template>

        <template v-slot:item.acciones="{ item }">
          <v-btn small icon @click="verDetalle(item)" title="Ver detalles">
            <v-icon>mdi-eye</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8 grey--text">
            No hay convenios registrados
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom right timeout="4000">
      {{ snackbar.mensaje }}
    </v-snackbar>

    <!-- Modal Detalle del Convenio -->
    <v-dialog v-model="dialogDetalle" max-width="700px">
      <v-card v-if="convenioDetalle" class="elevation-2">
        <v-card-title class="primary--text">
          Detalle del Convenio
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogDetalle = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-row class="mb-6">
            <v-col cols="12">
              <div class="text-caption font-weight-bold mb-2">CAMPO</div>
              <div class="text-body2 mb-1">{{ convenioDetalle.campo_detail?.name }}</div>
              <div class="text-caption grey--text">{{ convenioDetalle.campo_detail?.location }}</div>
              <div class="text-caption grey--text mt-2">
                <strong>Teléfono:</strong> {{ convenioDetalle.campo_detail?.phone }}
              </div>
              <div class="text-caption grey--text">
                <strong>Email:</strong> {{ convenioDetalle.campo_detail?.email }}
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row class="mb-6">
            <v-col cols="12">
              <div class="text-caption font-weight-bold mb-2">TIPO DE CONVENIO</div>
              <v-chip color="primary" text-color="white" small class="mb-2">
                {{ convenioDetalle.tipo_detail?.nombre }}
              </v-chip>
              <div class="text-caption mb-2">{{ convenioDetalle.tipo_detail?.descripcion }}</div>
              <div class="text-subtitle2 primary--text font-weight-bold">
                Descuento: {{ convenioDetalle.tipo_detail?.descuento }}%
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row class="mb-6">
            <v-col cols="12">
              <div class="text-caption font-weight-bold mb-2">SOCIO</div>
              <div class="d-flex align-center">
                <v-avatar size="48" class="mr-3" color="primary" text-color="white">
                  <span>{{ (convenioDetalle.socio_detail?.first_name?.[0] || '') + (convenioDetalle.socio_detail?.last_name?.[0] || '') }}</span>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">
                    {{ convenioDetalle.socio_detail?.first_name }} {{ convenioDetalle.socio_detail?.last_name }}
                  </div>
                  <div class="text-caption grey--text">{{ convenioDetalle.socio_detail?.email }}</div>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-row>
            <v-col cols="6">
              <div class="text-caption font-weight-bold mb-2">ESTADO</div>
              <v-chip
                :color="convenioDetalle.estado === 'activo' ? 'success' : convenioDetalle.estado === 'vencido' ? 'warning' : 'error'"
                text-color="white"
                small
              >
                {{ convenioDetalle.estado }}
              </v-chip>
            </v-col>
            <v-col cols="6">
              <div class="text-caption font-weight-bold mb-2">FECHA CREACIÓN</div>
              <div class="text-caption">{{ new Date(convenioDetalle.created_at).toLocaleDateString('es-ES') }}</div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDetalle = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'GolfConveniosList',
  data () {
    return {
      snackbar: {
        show: false,
        mensaje: '',
        color: 'success'
      },
      filtros: {
        campo: null,
        tipo: null,
        estado: null,
        socio: null
      },
      headers: [
        { text: 'Fecha', value: 'created_at', width: '100px' },
        { text: 'Campo', value: 'campo_detail.name' },
        { text: 'Tipo', value: 'tipo_detail.nombre', width: '120px' },
        { text: 'Socio', value: 'socio_detail.first_name' },
        { text: 'Estado', value: 'estado', width: '100px' },
        { text: 'Acciones', value: 'acciones', sortable: false, width: '100px' }
      ],
      tiposConveniosMap: {
        1: { nombre: 'Cortesía', color: 'success' },
        2: { nombre: 'Descuento Especial', color: 'info' },
        3: { nombre: 'Intercambio', color: 'warning' },
        4: { nombre: 'Privilegio', color: 'primary' }
      },
      dialogDetalle: false,
      convenioDetalle: null,
      cargandoDetalle: false
    }
  },
  computed: {
    ...mapState('golfConvenios', {
      convenios: 'convenios',
      campos: 'campos',
      tipos: 'tipos',
      loading: 'loading',
      error: 'error'
    }),
    tiposConvenios () {
      return this.tipos || []
    },
    conveniosFiltrados () {
      return this.convenios.filter(convenio => {
        const cumpleEstado = !this.filtros.estado || convenio.estado === this.filtros.estado
        const cumpleSocio = !this.filtros.socio ||
          (convenio.socio_detail?.first_name + ' ' + convenio.socio_detail?.last_name)
            .toLowerCase()
            .includes(this.filtros.socio.toLowerCase())
        return cumpleEstado && cumpleSocio
      })
    }
  },
  mounted () {
    this.cargarDatos()
  },
  methods: {
    ...mapActions('golfConvenios', ['fetchConvenios', 'fetchTipos', 'fetchCampos', 'cancelarConvenio']),
    async cargarDatos () {
      try {
        await Promise.all([
          this.fetchConvenios(),
          this.fetchTipos(),
          this.fetchCampos()
        ])
      } catch (error) {
        this.mostrarSnackbar('Error al cargar datos', 'error')
      }
    },
    aplicarFiltros () {
      // Filtrado en computed
    },
    getNombreTipo (tipoId) {
      return this.tiposConveniosMap[tipoId]?.nombre || 'Desconocido'
    },
    getNombreCampo (campoId) {
      return this.campos.find(c => c.id === campoId)?.name || 'Desconocido'
    },
    getTipoColor (tipoId) {
      return this.tiposConveniosMap[tipoId]?.color || 'grey'
    },
    async verDetalle (item) {
      this.cargandoDetalle = true
      try {
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.getConvenio(item.id)
        this.convenioDetalle = response.data
        this.dialogDetalle = true
      } catch (error) {
        console.error('Error al cargar detalle del convenio:', error)
        this.mostrarSnackbar('Error al cargar detalle del convenio', 'error')
      } finally {
        this.cargandoDetalle = false
      }
    },
    async cancelarConvenioHandler (item) {
      const nombreSocio = item.socio_detail?.first_name + ' ' + item.socio_detail?.last_name
      if (confirm(`¿Estás seguro de que deseas cancelar el convenio de ${nombreSocio}?`)) {
        try {
          await this.cancelarConvenio(item.id)
          this.mostrarSnackbar('✓ Convenio cancelado', 'success')
        } catch (error) {
          this.mostrarSnackbar('Error al cancelar convenio', 'error')
        }
      }
    },
    mostrarSnackbar (mensaje, color) {
      this.snackbar = {
        show: true,
        mensaje,
        color
      }
    }
  }
}
</script>
